import { memo, useCallback, useState } from 'react'

import { Delete, MoreVert } from '@mui/icons-material'
import {
  Box,
  CircularProgress,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material'
import { PersonalAccessToken } from '@nativewaves/nw-cloud-sdk/identity'
import { formatDistanceToNow, intlFormatDistance, isValid, isWithinInterval, parseISO } from 'date-fns'
import { useTranslation } from 'react-i18next'

import { usePersonalAccessTokenDeleteMutation } from 'hooks/mutations/identity/User'
import { validEndDate, validStartDate } from 'utils'

export type Props = {
  token: PersonalAccessToken
}

/**
 * One calendar link, as far as it can still be shown: the URL was only ever displayed once, so what
 * is left to do with it here is see whether anything is still fetching it, and revoke it.
 */
const TokenRowRaw = ({ token }: Props) => {
  const { t } = useTranslation(['common', 'domain'])

  const deleteMutation = usePersonalAccessTokenDeleteMutation()

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const handleDelete = useCallback(() => {
    deleteMutation.mutate({ userId: token.userId, tokenId: token.id })
    setMenuAnchor(null)
    setConfirmDelete(false)
  }, [deleteMutation, token.id, token.userId])

  const handleCloseMenu = useCallback(() => {
    setMenuAnchor(null)
    setConfirmDelete(false)
  }, [])

  // An "unlimited" expiry is stored as a date far outside any sane range rather than as null.
  const isWithinValidRange = (value: string) =>
    isValid(parseISO(value)) && isWithinInterval(parseISO(value), { start: validStartDate, end: validEndDate })

  const expiry = isWithinValidRange(token.expiryTime)
    ? t('domain:Workspace.Settings.Calendar.expires', {
        distance: formatDistanceToNow(parseISO(token.expiryTime), { addSuffix: true }),
      })
    : t('domain:Workspace.Settings.Calendar.neverExpires')

  const lastUsed = isWithinValidRange(token.lastUseTime)
    ? t('domain:Workspace.Settings.Calendar.lastFetched', {
        distance: intlFormatDistance(parseISO(token.lastUseTime), new Date()),
      })
    : t('domain:Workspace.Settings.Calendar.neverFetched')

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        py: 0.75,
        px: 1.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="heading-sm" noWrap>
          {token.name || t('domain:Workspace.Settings.Calendar.unnamedLink')}
        </Typography>
        <Typography variant="text-sm" color="text.secondary" noWrap>
          {`${expiry} · ${lastUsed}`}
        </Typography>
      </Box>

      {deleteMutation.isPending ? (
        <CircularProgress size={16} />
      ) : (
        <>
          <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)}>
            <MoreVert fontSize="small" />
          </IconButton>
          <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={handleCloseMenu}>
            {!confirmDelete ? (
              <MenuItem onClick={() => setConfirmDelete(true)}>
                <ListItemIcon>
                  <Delete fontSize="small" color="error" />
                </ListItemIcon>
                <ListItemText>{t('domain:Workspace.Settings.Calendar.revoke')}</ListItemText>
              </MenuItem>
            ) : (
              <MenuItem onClick={handleDelete}>
                <ListItemIcon>
                  <Delete fontSize="small" color="error" />
                </ListItemIcon>
                <ListItemText sx={{ color: 'error.main' }}>
                  {t('domain:Workspace.Settings.Calendar.confirmRevoke')}
                </ListItemText>
              </MenuItem>
            )}
          </Menu>
        </>
      )}
    </Box>
  )
}

export const TokenRow = memo(TokenRowRaw)
